import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { idTypeOptions } from '@roc/feature-loans';
import { CurrencyField, DateField, SelectField, TextField } from '@roc/ui';
import { citizenshipStatusOptions, formatDate, parseDate } from '@roc/feature-utils';
import { IdentityAddressField, stateOptions } from './identityAddressField';
import { serverValue } from './identityReviewValues';

const withCurrentValueOption = (options: { value: string; label: string }[], value: any) =>
  value && !options.some(option => option.value === value) ? [{ value, label: value }, ...options] : options;

export const IdentityEditModal = observer(({ open }: { open: boolean }) => {
  const { docReviewStoreV2 } = useStore();
  const { identityFormStore, document } = docReviewStoreV2;
  const { values } = identityFormStore;

  useEffect(() => {
    if (open) identityFormStore.load(docReviewStoreV2.docReviewData, document?.borrowerId);
  }, [open]);

  const errorOf = (fieldName: string) => identityFormStore.form.fields[fieldName]?.error;
  const onChange = (fieldName: string, value: any) => identityFormStore.onEditFieldChange(fieldName, value);

  const dateField = (fieldName: string, label: string, props = {}) => (
    <DateField
      label={label}
      value={values[fieldName] ? parseDate(values[fieldName]) : null}
      onChange={value => onChange(fieldName, value ? formatDate(value, 'yyyy-MM-dd') : null)}
      format="MM/dd/yyyy"
      testId={fieldName}
      inputVariant="outlined"
      fullWidth
      standaloneLabel
      error={!!errorOf(fieldName)}
      errorText={errorOf(fieldName)}
      {...props}
    />
  );

  const selectField = (fieldName: string, label: string, options, props = {}) => (
    <SelectField
      label={label}
      value={values[fieldName] ?? ''}
      onChange={value => onChange(fieldName, value)}
      options={options}
      name={fieldName}
      testId={fieldName}
      variant="outlined"
      fullWidth
      standaloneLabel
      error={!!errorOf(fieldName)}
      helperText={errorOf(fieldName)}
      {...props}
    />
  );

  const currencyField = (fieldName: string, label: string, value, props = {}) => (
    <CurrencyField
      label={label}
      value={value ?? null}
      onChange={(_name, value) => onChange(fieldName, value)}
      name={fieldName}
      testId={fieldName}
      variant="outlined"
      fullWidth
      standaloneLabel
      error={!!errorOf(fieldName)}
      errorText={errorOf(fieldName)}
      {...props}
    />
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {/* Display only - the name isn't part of the form, so it's never saved. */}
        <TextField
          label="Name"
          value={serverValue(docReviewStoreV2.docReviewData ?? {}, 'fullName') ?? ''}
          testId="fullName"
          variant="outlined"
          fullWidth
          standaloneLabel
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        {dateField('dateOfBirth', 'Date of Birth', { required: true, disableFuture: true })}
      </Grid>
      <Grid item xs={12}>
        <IdentityAddressField store={identityFormStore} />
      </Grid>
      <Grid item xs={12}>
        {selectField('idType', 'ID Type', withCurrentValueOption(idTypeOptions, values.idType))}
      </Grid>
      <Grid item xs={12}>
        {selectField('issuingState', 'ID State', withCurrentValueOption(stateOptions, values.issuingState))}
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="ID Number"
          value={values.idNumber ?? ''}
          onChange={e => onChange('idNumber', e.target.value)}
          name="idNumber"
          testId="idNumber"
          variant="outlined"
          fullWidth
          standaloneLabel
          error={!!errorOf('idNumber')}
          helperText={errorOf('idNumber')}
        />
      </Grid>
      <Grid item xs={12}>
        {selectField('citizenshipStatus', 'Citizenship Status', citizenshipStatusOptions, { required: true })}
      </Grid>
      <Grid item xs={12}>
        {dateField('expiryDate', 'Expiration Date')}
      </Grid>
      <Grid item xs={12}>
        {currencyField('assets', 'Asset', values.assets)}
      </Grid>
      <Grid item xs={12}>
        {currencyField('liabilities', 'Liability', values.liabilities)}
      </Grid>
      <Grid item xs={12}>
        {/* Computed, not a form field - CurrencyField fires onChange when its value changes, so don't forward it. */}
        {currencyField('netWorth', 'Total Net Worth', identityFormStore.netWorth, { disabled: true, onChange: undefined })}
      </Grid>
    </Grid>
  );
});
